import * as React from 'react';
import { ButtonProps } from './Button';
import glamorous from 'glamorous';
import '../index.css';
import { browserHistory } from './AppContent';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { ChangedPageEnum } from './common/common';
import DialogBox from '../Dashboard/Component/DialogBox';
import { IconButton, Tooltip, withStyles, SvgIcon } from '@material-ui/core';
import { SkeletonLoader } from './loading/SkeletonLoader';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';

const PushPinOutlinedIcon = (props: { style?: React.CSSProperties }) => (
    <SvgIcon style={props.style} viewBox="0 0 24 24">
        {/* outlined pin, rotated 45° so the point faces the corner — classic "unpin" look */}
        <g transform="rotate(45 12 12)">
            <path d="M17,2H7C6.45,2,6,2.45,6,3s0.45,1,1,1h1v5c0,1.66-1.34,3-3,3H4.5C4.22,12,4,12.22,4,12.5
                     S4.22,13,4.5,13h6.53V20l1,1l1-1v-7H19.5c0.28,0,0.5-0.22,0.5-0.5S19.78,12,19.5,12H19
                     c-1.66,0-3-1.34-3-3V4h1c0.55,0,1-0.45,1-1S17.55,2,17,2z
                     M14,4v5c0,1.12,0.37,2.16,1,3H9c0.65-0.86,1-1.9,1-3V4H14z"/>
        </g>
    </SvgIcon>
);

const PushPinIcon = (props: { style?: React.CSSProperties }) => (
    <SvgIcon style={props.style} viewBox="0 0 24 24">
        {/* filled pin, rotated 45° — "pinned" state */}
        <g transform="rotate(45 12 12)">
            <path d="M17,2H7C6.45,2,6,2.45,6,3s0.45,1,1,1h1v5c0,1.66-1.34,3-3,3H4.5C4.22,12,4,12.22,4,12.5
                     S4.22,13,4.5,13H11v7l1,1l1-1v-7h6.5c0.28,0,0.5-0.22,0.5-0.5S20.28,12,20,12h-0.5
                     C17.34,12,16,10.66,16,9V4h1c0.55,0,1-0.45,1-1S17.55,2,17,2z"/>
        </g>
    </SvgIcon>
);

export type DockMode = 'closed' | 'full' | 'semi';

/* ─── constants ──────────────────────────────────────────────────── */

// Single source of truth for the fixed header's height — also consumed by
// AppContent.tsx (.header height / .custom-site-content paddingTop).
export const HEADER_HEIGHT = '64px';

const SIDEBAR_BG       = '#ffffff';
const SIDEBAR_BORDER   = 'rgba(0,0,0,0.09)';
const SIDEBAR_TEXT     = '#23253B';
const SIDEBAR_TEXT_HOV = '#5173FF';
const SIDEBAR_ICON_CLR = '#5173FF';
// Explicit request: restyle to match the rest of the app's now-established look (Quick View's
// Enquiry Follow-ups Due cards, Document AI, the notification centre) - same accent blue
// (#5173FF, already this sidebar's own SIDEBAR_ACCENT) and the same soft-tint technique
// (MOBILE_ACCENT_TEAL_SOFT in YourEnquiriesWidget.tsx uses this identical alpha-suffix hex).
// SIDEBAR_HOV_BG was previously '#FFFFFF' - the same as the resting background, so hovering a
// row barely changed anything. The active row's own background (#5173FF1F) lives in App.css's
// own a.rv-nav-active rule instead of a constant here, since NavLink's activeClassName applies
// that class externally (outside this component's own props/styling).
const SIDEBAR_HOV_BG    = '#5173FF0F';
const SIDEBAR_ICON_CHIP_BG = '#5173FF1A';
const SIDEBAR_ACCENT   = '#5173FF';

// Exported so callers elsewhere that need to avoid overlapping the docked sidebar (e.g.
// AiChatMinimizedBar.tsx, positioned fixed between the sidebar and the Quick View panel) can
// compute their own left inset from the same values, rather than duplicating the numbers.
export const FULL_WIDTH = '230px';
export const SEMI_WIDTH = '64px';

const NavTooltip = withStyles({
    tooltip: {
        fontSize:     '13px',
        padding:      '6px 10px',
        borderRadius: '4px',
    },
})(Tooltip);

/* ─── layout components ─────────────────────────────────────────── */

// flex: 1 fills the height propagated from <main> (which is itself flex: 1 in
// SiteParent). minHeight: 0 prevents flex children from overflowing.
// marginBottom: -15px extends the sidebar into <main>'s padding-bottom so the
// sidebar reaches the footer with no gap.
const DockedWrapper = glamorous.div({
    display:      'flex',
    width:        '100%',
    flex:         1,
    minHeight:    0,
    marginBottom: '-15px',
    position:     'relative',
});

const DockedSidebar = glamorous.div<{ semi?: boolean; closed?: boolean }>((props) => ({
    width:      props.closed ? '0px' : (props.semi ? SEMI_WIDTH : FULL_WIDTH),
    minWidth:   props.closed ? '0px' : (props.semi ? SEMI_WIDTH : FULL_WIDTH),
    opacity:    props.closed ? 0 : 1,
    flexShrink: 0,
    background: SIDEBAR_BG,
    boxShadow:  props.closed ? 'none' : '2px 0 8px rgba(0,0,0,0.10)',
    overflowY:  'auto',
    overflowX:  'hidden',
    display:    'flex',
    flexDirection: 'column',
    transition: 'width 0.2s ease, min-width 0.2s ease, box-shadow 0.2s ease, opacity 0.15s ease',
    // sticky so it stays in view when the page scrolls on small screens.
    // top: HEADER_HEIGHT keeps it below the fixed header (same height).
    // No alignSelf override — let the sidebar stretch to DockedWrapper's full height
    // so it fills the content area visually. Sticky still fires against the viewport
    // scroll (not the flex parent), so the sidebar never overlaps the header logo.
    // zIndex: 8 keeps it below the header (z-index: 9) so the header always wins.
    position:   'sticky',
    top:        HEADER_HEIGHT,
    zIndex:     1008,
}));

// Fixed to the viewport so it can never bleed into the header regardless of
// screen size. top: HEADER_HEIGHT matches the fixed header height. z-index: 8 keeps it
// below the header (z-index: 9) so the header stays fully visible and interactive.
const OverlayPanel = glamorous.div<{ visible?: boolean }>((props) => ({
    position:      'fixed',
    top:           HEADER_HEIGHT,
    left:          0,
    width:         FULL_WIDTH,
    height:        `calc(100vh - ${HEADER_HEIGHT})`,
    background:    SIDEBAR_BG,
    boxShadow:     '2px 0 16px rgba(0,0,0,0.14)',
    zIndex:        1008,
    overflowY:     'auto',
    overflowX:     'hidden',
    display:       'flex',
    flexDirection: 'column' as 'column',
    transform:     props.visible ? 'translateX(0)' : `translateX(-${FULL_WIDTH})`,
    transition:    'transform 0.25s ease',
}));

// Backdrop: fixed, also below the header so the header stays interactive.
const OverlayBackdrop = glamorous.div({
    position:   'fixed',
    top:        HEADER_HEIGHT,
    left:       0,
    width:      '100vw',
    height:     `calc(100vh - ${HEADER_HEIGHT})`,
    background: 'rgba(0,0,0,0.35)',
    zIndex:     1007,
});

// display: flex (row, not column) so the single page child stretches to fill
// DockedWrapper's full height via align-items: stretch (the default).
// flex: 1 on direct div children fills the full width.
const DockedContent = glamorous.div({
    flex:          1,
    minWidth:      0,
    display:       'flex',
    // paddingBottom: '15px',
    overflowY:     'auto',
    overflowX:     'hidden',
    position: 'relative',
    [`& > div`]:   { flex: 1, minWidth: 0 },
});

/* ─── shared sidebar internals ──────────────────────────────────── */

const SidebarHeader = glamorous.div<{ semi?: boolean }>((props) => ({
    display:        'flex',
    alignItems:     'center',
    justifyContent: props.semi ? 'center' : 'space-between',
    gap:            '8px',
    padding:        '0 10px',
    borderBottom:   `1px solid ${SIDEBAR_BORDER}`,
    minHeight:      '56px',
    flexShrink:     0,
}));

const SearchInputWrap = glamorous.div({
    position:   'relative',
    display:    'flex',
    alignItems: 'center',
    flex:       1,
});

const SearchInput = glamorous.input({
    width:        '100%',
    background:   'rgba(0,0,0,0.04)',
    border:       '1px solid rgba(0,0,0,0.12)',
    borderRadius: '6px',
    color:        SIDEBAR_TEXT,
    fontSize:     '12px',
    padding:      '6px 28px 6px 10px',
    outline:      'none',
    boxSizing:    'border-box' as 'border-box',
    '::placeholder': { color: 'rgba(0,0,0,0.32)' },
    ':focus': {
        borderColor: SIDEBAR_ACCENT,
        background:  'rgba(81,115,255,0.06)',
    },
});

const SearchClearBtn = glamorous.button({
    position:   'absolute',
    right:      '6px',
    background: 'none',
    border:     'none',
    padding:    '0',
    cursor:     'pointer',
    color:      'rgba(0,0,0,0.32)',
    display:    'flex',
    alignItems: 'center',
    lineHeight: '1',
    ':hover': { color: 'rgba(0,0,0,0.65)' },
});

// position:relative + isolation:isolate so NavActiveIndicator below can sit absolutely behind the rows (z-index -1
// stays inside this list instead of falling behind the sidebar's own background).
const NavList = glamorous.div({
    paddingTop:    '6px',
    paddingBottom: '16px',
    flex:          1,
    position:      'relative',
    isolation:     'isolate',
});

// The active row's highlight: one pill that slides to whichever row is active, instead of each row's own
// background switching on/off (which looked like a plain colour change when moving between menu items).
const NavActiveIndicatorBar = glamorous.div<{ semi?: boolean }>((props) => ({
    position:        'absolute',
    top:             0,
    left:            props.semi ? '8px' : '2px',
    right:           props.semi ? '8px' : '2px',
    zIndex:          -1,
    borderRadius:    '8px',
    backgroundColor: '#5173FF1F',
    pointerEvents:   'none',
    '@media (prefers-reduced-motion: reduce)': { transition: 'none !important' },
}));

interface NavActiveIndicatorProps {
    semi: boolean;
    // changes whenever the set of rows changes (menu search), so the pill is re-measured
    layoutKey: string;
}

const NavActiveIndicator: React.FC<NavActiveIndicatorProps> = ({ semi, layoutKey }) => {
    const location = useLocation();
    const indicatorRef = React.useRef<HTMLDivElement>(null);
    const [position, setPosition] = React.useState<{ top: number; height: number } | null>(null);
    // the pill only animates after its first placement, so it doesn't slide in from the top on first load
    const [isAnimated, setIsAnimated] = React.useState(false);

    const measureActiveItem = React.useCallback(() => {
        const navList = indicatorRef.current && indicatorRef.current.parentElement;
        const activeItem = navList && navList.querySelector('a.rv-nav-active .rv-nav-item') as HTMLElement | null;
        setPosition(activeItem ? { top: activeItem.offsetTop, height: activeItem.offsetHeight } : null);
    }, []);

    // Drops the click-time "pending" look (see the click handler below) once the router has caught up.
    const clearPendingNavigation = React.useCallback(() => {
        const navList = indicatorRef.current && indicatorRef.current.parentElement;
        if (navList) {
            navList.classList.remove('rv-nav-switching');
            navList.querySelectorAll('a.rv-nav-pending').forEach((link) => link.classList.remove('rv-nav-pending'));
        }
    }, []);

    React.useLayoutEffect(() => {
        clearPendingNavigation();
        measureActiveItem();
        // rows resize (padding transition) while the sidebar collapses/expands - measure once more when it settles
        const settleTimer = window.setTimeout(measureActiveItem, 260);
        return () => window.clearTimeout(settleTimer);
    }, [location.pathname, semi, layoutKey]);

    React.useEffect(() => {
        if (position) {
            setIsAnimated(true);
        }
    }, [position]);

    // Heavy pages (Enquiries) render synchronously in the same task as the click, so the pill - which used to wait
    // for the route change - started moving late and looked stuck. Instead the pill moves on the click itself, the
    // navigation is held back two animation frames so that first frame is painted, then the click is replayed.
    React.useEffect(() => {
        const navList = indicatorRef.current && indicatorRef.current.parentElement;
        if (!navList) {
            return undefined;
        }
        let isReplayingClick = false;
        const onNavClickCapture = (event: MouseEvent) => {
            if (isReplayingClick || event.defaultPrevented || event.button !== 0
                || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
                return;
            }
            const clickedLink = (event.target as HTMLElement).closest('a');
            const clickedItem = clickedLink && clickedLink.querySelector('.rv-nav-item') as HTMLElement | null;
            if (!clickedLink || !clickedItem || !navList.contains(clickedLink)) {
                return;
            }
            event.preventDefault();
            event.stopPropagation();
            setPosition({ top: clickedItem.offsetTop, height: clickedItem.offsetHeight });
            // The dark icon chip is also styled from the router's active class, which only lands after the new page
            // has rendered (slow on Enquiries) - so give the clicked row, and take from the old one, the same look now.
            navList.classList.add('rv-nav-switching');
            clickedLink.classList.add('rv-nav-pending');
            window.requestAnimationFrame(() => window.requestAnimationFrame(() => {
                isReplayingClick = true;
                clickedLink.click();
                isReplayingClick = false;
                // navigation can be refused (e.g. the "leave the page?" prompt) - then put the highlight back
                navigationFallbackTimer = window.setTimeout(() => {
                    clearPendingNavigation();
                    measureActiveItem();
                }, 2500);
            }));
        };
        let navigationFallbackTimer: number | undefined;
        navList.addEventListener('click', onNavClickCapture, true);
        return () => {
            navList.removeEventListener('click', onNavClickCapture, true);
            window.clearTimeout(navigationFallbackTimer);
        };
    }, [measureActiveItem, clearPendingNavigation]);

    return (
        <NavActiveIndicatorBar
            innerRef={indicatorRef}
            semi={semi}
            style={{
                opacity:    position ? 1 : 0,
                height:     position ? position.height : 0,
                transform:  `translateY(${position ? position.top : 0}px)`,
                // transform only (compositor-driven, keeps running while a heavy page renders); height changes
                // only when the sidebar collapses/expands, so it just snaps
                transition: isAnimated
                    ? 'transform 0.28s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.15s ease, left 0.2s ease, right 0.2s ease'
                    : 'none',
            }}
        />
    );
};

// Rounded "pill" row instead of the old flat left-border bar - same shape language as the
// rounded card/chip treatment used throughout Quick View and Document AI this session. Margin
// (rather than full-bleed) so the rounded corners actually read against the sidebar's own edges.
const NavItem = glamorous.div<{ semi?: boolean }>((props) => ({
    display:        'flex',
    alignItems:     'center',
    justifyContent: props.semi ? 'center' : 'flex-start',
    gap:            props.semi ? '0' : '10px',
    padding:        props.semi ? '8px 0' : '5px 10px',
    margin:         props.semi ? '2px 8px' : '2px 2px',
    borderRadius:   '8px',
    cursor:         'pointer',
    color:          SIDEBAR_TEXT,
    fontSize:       '14px',
    fontWeight:     400,
    lineHeight:     '20px',
    transition:     'background-color 0.15s ease, color 0.12s ease, gap 0.2s ease, padding 0.2s ease',
    ':hover': {
        backgroundColor: SIDEBAR_HOV_BG,
        color:           SIDEBAR_TEXT_HOV,
    },
}));

// Same rounded icon-chip pattern as the icon chips used everywhere else now (Quick View
// widgets, Document AI header, the notification centre) - a soft tinted square behind the
// icon, rather than a bare icon floating on its own.
const NavIconWrap = glamorous.span({
    display:        'flex',
    alignItems:     'center',
    justifyContent: 'center',
    flexShrink:     0,
    width:          '30px',
    height:         '30px',
    borderRadius:   '8px',
    backgroundColor: SIDEBAR_ICON_CHIP_BG,
    color:          SIDEBAR_ICON_CLR,
    transition:     'background-color 0.15s ease, color 0.15s ease',
    [`& svg`]:      { width: '18px', height: '18px' },
});

// Always rendered (rather than conditionally mounted on `semi`) so it can fade/shrink
// smoothly in sync with the sidebar's own width transition, instead of popping in/out
// instantly while the container is still animating.
const NavLabel = glamorous.span<{ semi?: boolean }>((props) => ({
    display:      'block',
    flex:         props.semi ? '0 0 0px' : 1,
    whiteSpace:   'nowrap',
    overflow:     'hidden',
    textOverflow: 'ellipsis',
    maxWidth:     props.semi ? 0 : '180px',
    opacity:      props.semi ? 0 : 1,
    transition:   'opacity 0.15s ease, max-width 0.2s ease',
}));

const NoResults = glamorous.div({
    padding:   '12px 18px',
    color:     'rgba(0,0,0,0.38)',
    fontSize:  '13px',
    fontStyle: 'italic',
});

/* ─── types ──────────────────────────────────────────────────────── */

interface DrawerProps {
    visible?:    boolean;
    links?:      Array<LinkProps>;
    orientation?: 'left' | 'right' | 'top' | 'bottom';
    page?:       string;
    // tslint:disable-next-line:no-any
    drawerContent?: any;
    animation?:  'overlay' | 'push' | 'scale down' | 'uncover' | 'slide out' | 'slide along';
    width?:      'very thin' | 'thin' | 'wide' | 'very wide';
    handleSidebarHide?: (value: boolean) => void;
    className?:  string;
    dockMode?:   DockMode;
    onCycleDock?: () => void;
    isLoading?:  boolean;
}

export interface LinkProps {
    iconKey?:  React.ReactNode;
    label?:    string;
    // tslint:disable-next-line:no-any
    linkTo?:   any;
    visible:   boolean;
    onClick?:  (event: React.MouseEvent<HTMLButtonElement>, data: ButtonProps) => void;
    /** Optional extra active check — return true to highlight this item regardless of the current URL match */
    isActiveFn?: (pathname: string) => boolean;
}

interface DrawerState {
    visible?:    boolean;
    showConfirm: boolean;
    linkTo?:     string;
    searchText:  string;
}

/* ─── component ─────────────────────────────────────────────────── */

export class RvMainDrawer extends React.Component<DrawerProps, DrawerState> {

    constructor(props: DrawerProps, context?: DrawerState) {
        super(props, context);
        this.handleSidebarHide = this.handleSidebarHide.bind(this);
        this.onLeaveConfirm    = this.onLeaveConfirm.bind(this);
        this.onSearchChange    = this.onSearchChange.bind(this);
        this.state = { visible: this.props.visible, showConfirm: false, searchText: '' };
    }

    // tslint:disable-next-line:no-any
    public UNSAFE_componentWillReceiveProps(nextProps: DrawerProps, nextContext: any): void {
        if (nextProps.visible !== this.state.visible) {
            this.setState({ visible: nextProps.visible });
        }
        // Clear search when collapsing to icon-only (semi) or closing
        if (nextProps.dockMode === 'semi' && this.props.dockMode !== 'semi') {
            this.setState({ searchText: '' });
        }
    }

    private onSearchChange(e: React.ChangeEvent<HTMLInputElement>) {
        this.setState({ searchText: e.target.value });
    }

    private dockButton() {
        if (!this.props.onCycleDock) { return null; }
        const mode = this.props.dockMode || 'closed';
        let icon: JSX.Element;
        let title: string;
        if (mode === 'closed') {
            icon  = <PushPinOutlinedIcon style={{ fontSize: 17 }} />;
            title = 'Keep sidebar open';
        } else if (mode === 'full') {
            icon  = <PushPinIcon style={{ fontSize: 17 }} />;
            title = 'Collapse to icons';
        } else {
            icon  = <ChevronLeftIcon style={{ fontSize: 17 }} />;
            title = 'Close sidebar';
        }
        return (
            <IconButton
                size="small"
                onClick={this.props.onCycleDock}
                title={title}
                style={{ color: '#A8AABA' }}
            >
                {icon}
            </IconButton>
        );
    }

    private renderLinks(semi: boolean) {
        const { searchText } = this.state;
        const query = searchText.trim().toLowerCase();

        if (this.props.isLoading) {
            const skeletonWidths = ['70%', '55%', '80%', '60%', '75%', '50%', '65%'];
            return (
                <NavList>
                    {skeletonWidths.map((width, idx) => (
                        <NavItem key={idx} semi={semi} style={{ pointerEvents: 'none' }}>
                            <NavIconWrap>
                                <SkeletonLoader variant="shape" shape="circle" width={18} height={18} />
                            </NavIconWrap>
                            {!semi && (
                                <NavLabel>
                                    <SkeletonLoader variant="shape" shape="text" width={width} />
                                </NavLabel>
                            )}
                        </NavItem>
                    ))}
                </NavList>
            );
        }

        const links = (this.props.links || [])
            .filter(link => link.visible)
            .filter(link => !query || (link.label || '').toLowerCase().includes(query));

        return (
            <NavList>
                <NavActiveIndicator semi={semi} layoutKey={`${query}|${links.length}`} />
                {links.length > 0
                    ? links.map((link, idx) => (
                        <NavTooltip
                            key={'ML' + idx.toString()}
                            title={semi ? (link.label || '') : ''}
                            placement="right"
                            disableHoverListener={!semi}
                            disableFocusListener={!semi}
                            disableTouchListener={!semi}
                        >
                            <NavLink
                                to={link.linkTo}
                                activeClassName="rv-nav-active"
                                isActive={(match, location) => !!(match || (link.isActiveFn && link.isActiveFn(location.pathname)))}
                                style={{ textDecoration: 'none', color: 'inherit', display: 'block' }}
                            >
                                <NavItem semi={semi} className="rv-nav-item">
                                    <NavIconWrap className="rv-nav-icon-chip">
                                        {link.iconKey}
                                    </NavIconWrap>
                                    <NavLabel semi={semi}>{link.label}</NavLabel>
                                </NavItem>
                            </NavLink>
                        </NavTooltip>
                    ))
                    : query
                        ? <NoResults>No results for "{searchText}"</NoResults>
                        : undefined
                }
                {this.props.drawerContent}
            </NavList>
        );
    }

    private renderSidebarBody(semi: boolean) {
        return (
            <>
                <SidebarHeader semi={semi}>
                    {!semi && (
                        <SearchInputWrap>
                            <SearchInput
                                type="text"
                                placeholder="Search menu..."
                                value={this.state.searchText}
                                onChange={this.onSearchChange}
                            />
                            {this.state.searchText && (
                                <SearchClearBtn
                                    onClick={() => this.setState({ searchText: '' })}
                                    title="Clear search"
                                >
                                    &#x2715;
                                </SearchClearBtn>
                            )}
                        </SearchInputWrap>
                    )}
                    {this.dockButton()}
                </SidebarHeader>
                {this.renderLinks(semi)}
            </>
        );
    }

    render() {
        const confirmDialog = this.state.showConfirm && (
            <DialogBox
                title="Page"
                content={'Are you sure you want to leave the page?'}
                show={this.state.showConfirm}
                isAgree={this.onLeaveConfirm}
                disAgreeLabel={'No'}
                agreeLabel={'Yes'}
                disablePortal={true}
            />
        );

        const mode    = this.props.dockMode || 'closed';
        const visible = !!this.state.visible;
        const semi    = mode === 'semi';

        return (
            <>
                {confirmDialog}
                <DockedWrapper className="drawergrid">
                    {/* Docked sidebar – flex item that pushes content right. Always mounted
                        (rather than conditionally removed when closed) so collapsing all the
                        way down animates smoothly instead of the sidebar just disappearing. */}
                    <DockedSidebar semi={semi} closed={mode === 'closed'} className={'main-sidebarmenu ' + (this.props.className || '')}>
                        {this.renderSidebarBody(semi)}
                    </DockedSidebar>

                    {/* Overlay sidebar – absolutely positioned, doesn't affect layout */}
                    {mode === 'closed' && (
                        <>
                            {visible && <OverlayBackdrop onClick={this.handleSidebarHide} />}
                            <OverlayPanel
                                visible={visible}
                                className={'main-sidebarmenu ' + (this.props.className || '')}
                            >
                                {this.renderSidebarBody(false)}
                            </OverlayPanel>
                        </>
                    )}

                    {/* Content – stable key prevents unmount when mode changes */}
                    <DockedContent key="rv-main-content" className="main-sidebarpusher">
                        {this.props.children}
                    </DockedContent>
                </DockedWrapper>
            </>
        );
    }

    private onLeaveConfirm(agree: boolean) {
        if (agree && this.state.linkTo) {
            localStorage.removeItem(ChangedPageEnum.DashboardLayoutConfiguration);
            browserHistory.push(this.state.linkTo);
        }
        this.setState({ showConfirm: false });
    }

    private handleSidebarHide() {
        this.setState({ visible: false, searchText: '' });
        if (this.props.handleSidebarHide) {
            this.props.handleSidebarHide(false);
        }
    }
}
